import { useState, useRef } from 'react';
import { Button, Input, Typography, Tooltip, message } from 'antd';
import { CopyOutlined, EditOutlined, DeleteOutlined, MessageOutlined, SmileOutlined } from '@ant-design/icons';
import SenderAvatar from './SenderAvatar';
import { renderWithEntityAvatars } from './EntityAvatar';
import EmojiPicker, { insertAtCaret } from './EmojiPicker';
import { friendlyTime, fullTime } from '../../utils/friendly_time';
import { useNow } from '../../hooks/use_now';

const { Text } = Typography;
const { TextArea } = Input;

const ACTION_STYLE = { padding: '0 2px', height: 'auto', fontSize: 11, color: 'var(--text-secondary)' };

/**
 * A single chat message: sender avatar, timestamp, copy/edit/delete actions,
 * content (or an inline editor), and an optional reply-count bar. Used by both
 * chat surfaces (entity channels and direct messages) and in both positions
 * (top-level list and thread panel) — the differences are all props:
 *
 * - own            highlight as the current user's message
 * - variantClass   class for non-own bubbles (defaults to the message role;
 *                  channels pass "assistant" to style every peer message alike)
 * - showVellbyLabel render the "Vellby" text beside the logo for assistant msgs
 * - editable       show edit/delete actions (and allow the inline editor)
 * - replyCount     when defined, render the reply-count bar (top-level list)
 * - onToggleReaction(messageId, emoji) when given, show emoji reactions
 *                  (msg.reactions, one {emoji, user_id} per row) and a react
 *                  button; currentUserId marks which chips are the viewer's
 *
 * Edit state is split intentionally: the PARENT owns which message is open
 * (isEditing) so only one edits at a time; the bubble owns the draft text so
 * the parent doesn't carry per-keystroke state.
 */
export default function MessageBubble({
    msg,
    own,
    active = false,
    variantClass,
    showVellbyLabel = false,
    editable = false,
    isEditing = false,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
    replyCount,
    onToggleThread,
    currentUserId,
    onToggleReaction,
}) {
    const now = useNow();
    const [draft, setDraft] = useState(msg.content);
    const draftRef = useRef(null);
    const roleClass = own ? 'user' : (variantClass ?? msg.role);
    const textClass = msg.role === 'error' ? 'error-text' : msg.role === 'system' ? 'system-text' : '';

    const startEdit = () => { setDraft(msg.content); onStartEdit?.(msg); };
    const save = () => onSaveEdit?.(msg.message_id, draft.trim());

    // Group the flat rows into chips in first-reacted order: [{emoji, count, mine}].
    const reactionGroups = [];
    for (const r of msg.reactions || []) {
        let group = reactionGroups.find(g => g.emoji === r.emoji);
        if (!group) reactionGroups.push(group = { emoji: r.emoji, count: 0, mine: false });
        group.count += 1;
        if (r.user_id === currentUserId) group.mine = true;
    }
    const react = emoji => onToggleReaction?.(msg.message_id, emoji);

    return (
        <div className={`message-bubble ${roleClass}${active ? ' thread-active' : ''}`}>
            <div className="message-content" style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SenderAvatar senderId={msg.sender_id} senderName={msg.sender_name} role={msg.role} showLabel={showVellbyLabel} />
                    <Tooltip title={fullTime(msg.timestamp)}>
                        <Text className="message-timestamp">{friendlyTime(msg.timestamp, now)}</Text>
                    </Tooltip>
                    <span className="message-actions" onClick={e => e.stopPropagation()}>
                        <Button type="text" size="small" icon={<CopyOutlined />}
                            onClick={() => { navigator.clipboard.writeText(msg.content); message.success('Copied'); }}
                            style={ACTION_STYLE} />
                        {editable && own && !isEditing && (<>
                            <Button type="text" size="small" icon={<EditOutlined />}
                                onClick={startEdit} style={ACTION_STYLE} />
                            <Button type="text" size="small" icon={<DeleteOutlined />}
                                onClick={() => onDelete?.(msg.message_id)} style={ACTION_STYLE} />
                        </>)}
                    </span>
                </div>
                {editable && isEditing ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                        <TextArea
                            ref={draftRef}
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
                                if (e.key === 'Escape') onCancelEdit?.();
                            }}
                            autoSize={{ minRows: 1, maxRows: 6 }}
                            autoFocus
                            style={{ fontSize: 13 }}
                        />
                        <EmojiPicker size="small" onSelect={emoji => insertAtCaret(draftRef, draft, setDraft, emoji)} />
                        <Button size="small" type="primary" onClick={save}>Save</Button>
                        <Button size="small" onClick={() => onCancelEdit?.()}>Cancel</Button>
                    </div>
                ) : (
                    <Text className={textClass}>{renderWithEntityAvatars(msg.content)}</Text>
                )}
                {onToggleReaction && msg.message_id && (
                    <div className={`message-reactions${reactionGroups.length ? ' has-reactions' : ''}`} onClick={e => e.stopPropagation()}>
                        {reactionGroups.map(g => (
                            <Button
                                key={g.emoji}
                                size="small"
                                shape="round"
                                color={g.mine ? 'primary' : 'default'}
                                variant={g.mine ? 'filled' : 'outlined'}
                                onClick={() => react(g.emoji)}
                            >
                                {g.emoji} {g.count}
                            </Button>
                        ))}
                        <EmojiPicker
                            closeOnSelect
                            onSelect={emoji => {
                                // Picking an emoji you already reacted with must not un-react.
                                if (!reactionGroups.some(g => g.emoji === emoji && g.mine)) react(emoji);
                            }}
                            trigger={<Button type="text" size="small" shape="round" icon={<SmileOutlined />} className="message-react-add" aria-label="Add reaction" />}
                        />
                    </div>
                )}
                {replyCount !== undefined && (
                    <span className={`reply-count-bar${active ? ' active' : ''}`} onClick={onToggleThread}>
                        <MessageOutlined style={{ marginRight: 4 }} />
                        {replyCount > 0 ? `${replyCount} repl${replyCount === 1 ? 'y' : 'ies'}` : 'Reply'}
                    </span>
                )}
            </div>
        </div>
    );
}
